import { useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { FileSpreadsheet, Upload, Download, Trash2, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

/**
 * Bulk CSV upload of manual enrollments.
 * Accepts columns: student_name, student_curp, completed_at, final_exam_score, student_email (optional)
 * Preview in an editable table, choose the course, submit to `endpoint` (admin or teacher).
 */
export default function BulkEnrollmentUpload({ endpoint, courses = [] }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);           // parsed rows [{name, curp, date, score, email, _err}]
  const [courseId, setCourseId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [summary, setSummary] = useState(null);

  const validCount = useMemo(() => rows.filter((r) => !r._err).length, [rows]);
  const CURP_RE = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/;

  /**
   * Excel se sale con la suya: cada máquina exporta la fecha en un formato distinto.
   * Aceptamos DD/MM/YYYY (México/latín), MM/DD/YYYY (EE.UU.), YYYY-MM-DD (ISO),
   * DD-MM-YYYY y también fechas con separador punto. Devolvemos siempre YYYY-MM-DD.
   * Si el primer campo es > 12 es DD/MM sin ambigüedad; si ambos son ≤12 asumimos
   * DD/MM (default México).
   */
  const normalizeDate = (raw) => {
    if (!raw) return "";
    const s = String(raw).trim().replace(/[.\s]/g, "/").replace(/-/g, "/");
    // Already ISO?
    let m = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
    m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      let d = parseInt(m[1], 10), mm = parseInt(m[2], 10);
      if (d > 12) { /* DD/MM */ }
      else if (mm > 12) { const t = d; d = mm; mm = t; }
      // else ambiguous → default DD/MM (Mexican Excel export)
      const y = m[3];
      return `${y}-${String(mm).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    }
    return String(raw).trim();
  };

  const validateRow = (r) => {
    if (!r.student_name?.trim()) return "Nombre vacío";
    if (!r.student_curp || !CURP_RE.test(r.student_curp.toUpperCase())) return "CURP inválido";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(r.completed_at || "")) return "Fecha (usa DD/MM/AAAA o AAAA-MM-DD)";
    // Reject impossible calendar values (e.g. month=13)
    const [yy, mm, dd] = (r.completed_at || "").split("-").map(Number);
    if (!(yy > 1900 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31)) return "Fecha fuera de rango";
    const s = Number(r.final_exam_score);
    if (Number.isNaN(s) || s < 0 || s > 100) return "Calificación 0-100";
    return "";
  };

  const parseCsv = (text) => {
    // Normalise CRLF/CR line endings first (Excel on Windows uses CRLF) — otherwise
    // trailing "\r" survives on every cell and breaks validation silently.
    const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim());
    if (!lines.length) return [];
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
    // Column indices — allow either English or Spanish aliases
    const idx = (aliases) => aliases.map((a) => header.indexOf(a)).find((i) => i >= 0);
    const iName = idx(["student_name", "nombre_completo", "nombre"]);
    const iCurp = idx(["student_curp", "curp"]);
    const iDate = idx(["completed_at", "fecha_conclusion", "fecha"]);
    const iScore = idx(["final_exam_score", "calificacion", "calificación"]);
    const iEmail = idx(["student_email", "email", "correo"]);
    return lines.slice(1).map((ln) => {
      const cells = ln.split(",").map((c) => c.trim());
      const r = {
        student_name: iName >= 0 ? cells[iName] || "" : "",
        student_curp: iCurp >= 0 ? (cells[iCurp] || "").toUpperCase() : "",
        completed_at: normalizeDate(iDate >= 0 ? cells[iDate] || "" : ""),
        final_exam_score: iScore >= 0 ? Number(cells[iScore] || 100) : 100,
        student_email: iEmail >= 0 ? (cells[iEmail] || "").toLowerCase() : "",
      };
      r._err = validateRow(r);
      return r;
    });
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(""); setSummary(null);
    try {
      const text = await f.text();
      const parsed = parseCsv(text);
      if (!parsed.length) throw new Error("El archivo no contiene filas válidas.");
      setRows(parsed);
    } catch (e2) {
      setErr(e2.message || "No se pudo leer el CSV");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const updRow = (i, k, v) => {
    const next = rows.slice();
    next[i] = { ...next[i], [k]: k === "student_curp" ? (v || "").toUpperCase() : v };
    next[i]._err = validateRow(next[i]);
    setRows(next);
  };
  const rmRow = (i) => setRows(rows.filter((_, k) => k !== i));
  const clearAll = () => { setRows([]); setSummary(null); setErr(""); };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/admin/bulk-enrollments/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "om-pec-plantilla-carga-masiva.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submit = async () => {
    setErr(""); setSummary(null);
    if (!courseId) { setErr("Selecciona el curso al que pertenecen todos los alumnos"); return; }
    const valid = rows.filter((r) => !r._err).map(({ _err, ...clean }) => {
      // Drop empty student_email so backend Optional[EmailStr] does not reject ""
      if (!clean.student_email || !clean.student_email.trim()) {
        const { student_email, ...rest } = clean;
        return rest;
      }
      return clean;
    });
    if (!valid.length) { setErr("No hay filas válidas para procesar"); return; }
    setBusy(true);
    try {
      const r = await api.post(endpoint, { course_id: courseId, rows: valid });
      setSummary(r.data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bulk-upload-panel" className="bg-white border border-gris/30 rounded-xl p-6 shadow-sm space-y-4">
      <div className="flex items-center gap-2">
        <FileSpreadsheet className="w-5 h-5 text-naranja" />
        <h3 className="font-heading font-bold text-marino text-lg">Carga masiva por CSV</h3>
      </div>
      <p className="text-sm text-marino/70">
        Sube un archivo <b>.csv</b> con las columnas <code>student_name, student_curp, completed_at, final_exam_score, student_email (opcional)</code>.
        La fecha admite <b>DD/MM/AAAA</b> (formato Excel México) o <b>AAAA-MM-DD</b> (ISO). Puedes editar en la tabla antes de generar los certificados.
      </p>

      <div className="flex flex-wrap gap-3">
        <button data-testid="bulk-download-template" onClick={downloadTemplate} className="inline-flex items-center gap-2 border border-marino/20 text-marino px-3 py-2 rounded-lg text-sm hover:bg-marino/5">
          <Download className="w-4 h-4" /> Descargar plantilla
        </button>
        <input ref={fileRef} data-testid="bulk-file-input" type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
        <button data-testid="bulk-upload-btn" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 bg-marino text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-marino/90">
          <Upload className="w-4 h-4" /> Subir CSV
        </button>
        {rows.length > 0 && (
          <button onClick={clearAll} className="inline-flex items-center gap-2 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-sm hover:bg-red-50">
            <Trash2 className="w-4 h-4" /> Limpiar
          </button>
        )}
      </div>

      {err && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="text-sm text-marino/70">
              <b className="text-marino">{rows.length}</b> filas cargadas · <span className="text-green-600 font-semibold">{validCount} válidas</span> · <span className="text-red-600 font-semibold">{rows.length - validCount} con error</span>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-marino/70">Curso:</label>
              <select data-testid="bulk-course-select" value={courseId} onChange={(e) => setCourseId(e.target.value)} className="border border-gris rounded-lg px-3 py-1.5 text-sm bg-white">
                <option value="">— Selecciona —</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
              <button data-testid="bulk-submit-btn" onClick={submit} disabled={busy || !validCount || !courseId} className="inline-flex items-center gap-2 bg-naranja text-white px-4 py-1.5 rounded-lg text-sm font-semibold hover:bg-naranjaHover disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {busy ? "Generando..." : `Generar ${validCount} certificados`}
              </button>
            </div>
          </div>

          <div className="overflow-auto border border-gris/30 rounded-lg max-h-96">
            <table className="min-w-full text-xs">
              <thead className="bg-marino/5 sticky top-0">
                <tr>
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Nombre</th>
                  <th className="p-2 text-left">CURP</th>
                  <th className="p-2 text-left">Fecha</th>
                  <th className="p-2 text-left">Calif.</th>
                  <th className="p-2 text-left">Email (opcional)</th>
                  <th className="p-2 text-left">Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={`border-t border-gris/20 ${r._err ? "bg-red-50" : ""}`}>
                    <td className="p-1 text-marino/50">{i + 1}</td>
                    <td className="p-1"><input value={r.student_name} onChange={(e) => updRow(i, "student_name", e.target.value)} className="w-40 border border-gris/40 rounded px-1.5 py-1" /></td>
                    <td className="p-1"><input value={r.student_curp} onChange={(e) => updRow(i, "student_curp", e.target.value)} className="w-44 border border-gris/40 rounded px-1.5 py-1 font-mono uppercase" maxLength={18} /></td>
                    <td className="p-1"><input type="date" value={r.completed_at} onChange={(e) => updRow(i, "completed_at", e.target.value)} className="w-32 border border-gris/40 rounded px-1.5 py-1" /></td>
                    <td className="p-1"><input type="number" min="0" max="100" value={r.final_exam_score} onChange={(e) => updRow(i, "final_exam_score", e.target.value)} className="w-16 border border-gris/40 rounded px-1.5 py-1" /></td>
                    <td className="p-1"><input value={r.student_email} onChange={(e) => updRow(i, "student_email", e.target.value)} placeholder="—" className="w-48 border border-gris/40 rounded px-1.5 py-1" /></td>
                    <td className="p-1">{r._err ? <span className="text-red-600" title={r._err}>{r._err}</span> : <span className="text-green-600">OK</span>}</td>
                    <td className="p-1"><button onClick={() => rmRow(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {summary && (
        <div data-testid="bulk-summary" className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-green-800 font-heading font-bold">
            <CheckCircle2 className="w-5 h-5" /> {summary.created} certificados generados · {summary.failed} errores
          </div>
          {summary.errors?.length > 0 && (
            <ul className="mt-2 text-xs text-red-700 list-disc pl-5">
              {summary.errors.slice(0, 20).map((e, i) => <li key={i}>Fila {e.row} ({e.curp}): {e.error}</li>)}
            </ul>
          )}
          {summary.results?.some((x) => x.temporary_email_generated) && (
            <p className="mt-2 text-xs text-marino/70">
              Se generaron correos temporales <code>CURP@om-pec.com</code> con contraseña <code>om-pec2026</code> para los alumnos sin email.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
